// Ticket #250 — check chủ động cho `avatar doctor`: bắn MỘT POST thật tới
// `/api/telemetry` rồi đọc kết quả, để phân biệt 3 ca mà mọi check TĨNH đều mù.
//
// Vì sao cần: ca 2026-08-11 (#208) một thành viên 0 event all-time suốt nhiều ngày
// trong khi `avatar doctor` vẫn "23 check passed" — không check nào chạm đường gửi
// thật. #208 đã vá phía pack (counter + cảnh báo ở SessionStart) nhưng đó là tín hiệu
// BỊ ĐỘNG: phải chờ hook chạy đủ 3 lượt hỏng. Check này là đường CHỦ ĐỘNG, gõ là biết.
//
// Đọc mã Python + lý do phải dùng Python ở `telemetry-delivery-probe-python-source.ts`.

import { spawn } from "node:child_process";
import { join } from "node:path";
import type { CheckResult } from "../commands/doctor.js";
import {
  applyCaStoreRepair,
  type CaStoreRepairPlan,
  describeCaStoreRepairPlan,
  type PythonCaStoreState,
  planCaStoreRepair,
} from "./python-ca-store-repair.js";
import { TELEMETRY_DELIVERY_PROBE_PYTHON_SOURCE } from "./telemetry-delivery-probe-python-source.js";

const CHECK_NAME = "Telemetry delivery (probe POST)";
const PROBE_TIMEOUT_MS = 10_000;

// Câu này phải xuất hiện ở MỌI chỉ dẫn lỗi transport. Ca gốc mất 5 vòng chẩn đoán vì
// `curl` cùng url trả 200 (curl dùng CA của macOS, khác stack TLS của Python) — không
// nói thẳng thì người đọc lại đi đúng đường vòng cũ.
const CURL_CAVEAT =
  "⚠ `curl` tới cùng địa chỉ CÓ THỂ vẫn trả 200 — curl dùng chứng chỉ của hệ điều hành, khác stack TLS của Python. Đừng dùng curl để kết luận mạng ổn.";

export interface ProbeOutcome {
  kind: string;
  status?: number;
  error_type?: string;
  path?: string;
  // Ảnh chụp CA store, chỉ có ở nhánh `transport` (xem ca_info() bên Python).
  // Probe bản cũ không gửi ⇒ undefined, và khi đó KHÔNG được suy đoán trạng thái.
  ca_cafile?: string | null;
  ca_openssl_cafile?: string | null;
  ca_openssl_cafile_exists?: boolean;
  ca_ssl_cert_file_env?: string | null;
  certifi_path?: string | null;
  certifi_exists?: boolean;
}

/** Kết quả thô của probe → CheckResult. Thuần, không mạng ⇒ test được đầy đủ. */
export function classifyProbeOutcome(outcome: ProbeOutcome): Omit<CheckResult, "name"> {
  const base: { fixable: boolean } = { fixable: false };

  switch (outcome.kind) {
    case "http":
      return classifyHttpStatus(outcome.status, base);

    case "transport": {
      const t = outcome.error_type ?? "unknown";
      // Lỗi CA có tên lớp riêng (`SSLCertVerificationError`) nên chỉ dẫn được ĐÚNG
      // việc phải làm; các lỗi transport khác (DNS/proxy/refused) thì không.
      const isCert = t.includes("SSL") || t.includes("Certificate");
      if (!isCert) {
        return {
          ...base,
          status: "fail",
          detail: `KHÔNG kết nối được tới server (${t}). Kiểm tra mạng/VPN/proxy rồi thử lại. ${CURL_CAVEAT}`,
        };
      }
      // Ca 2026-08-18: chỉ dẫn CŨ ở đây là "chạy Install Certificates.command" — đúng
      // cái lệnh người dùng đã chạy, đã thấy "update complete", mà symlink vẫn không
      // có. Nên giờ đọc số đo thật rồi mới nói, và tự vá được thì gắn luôn fix.
      const plan = planCaStoreRepair(readCaStoreState(outcome));
      return {
        ...base,
        status: "fail",
        fixable: plan.kind === "symlink",
        fix: plan.kind === "symlink" ? () => runCaStoreFix(plan) : undefined,
        detail: `KHÔNG kết nối được tới server (${t}). ${describeCaStoreRepairPlan(plan)} ${CURL_CAVEAT}`,
      };
    }

    case "no_token":
      return {
        ...base,
        status: "fail",
        detail: "Không đọc được id_token — chạy `avatar login` rồi thử lại.",
      };

    case "token_read_failed":
      return {
        ...base,
        status: "fail",
        detail: `Đọc token hỏng (${outcome.error_type ?? "unknown"}) — chạy \`avatar login\` rồi thử lại.`,
      };

    // Không có pack thì KHÔNG có telemetry để đo ⇒ warn, không fail: đây là trạng
    // thái hợp lệ của workspace chưa `avatar sync`, không phải hỏng hóc.
    case "no_pack":
      return {
        ...base,
        status: "warn",
        detail: `Chưa cài pack (${outcome.path ?? "?"}) — không có telemetry để kiểm.`,
      };

    case "pack_import_failed":
      return {
        ...base,
        status: "fail",
        detail: `Pack có nhưng import token_reader hỏng (${outcome.error_type ?? "unknown"}) — thử \`avatar sync\`.`,
      };

    case "timeout":
      return {
        ...base,
        status: "warn",
        detail: `Probe quá ${PROBE_TIMEOUT_MS / 1000}s không phản hồi — mạng chậm hoặc bị chặn. ${CURL_CAVEAT}`,
      };

    case "no_python":
      return {
        ...base,
        status: "fail",
        detail:
          "Không chạy được `python3` — hook telemetry cũng dùng đúng binary này nên chúng cũng đang chết.",
      };

    default:
      return {
        ...base,
        status: "warn",
        detail: `Không phân loại được kết quả probe (${outcome.kind}${outcome.error_type ? `: ${outcome.error_type}` : ""}).`,
      };
  }
}

function classifyHttpStatus(
  status: number | undefined,
  base: { fixable: boolean },
): Omit<CheckResult, "name"> {
  switch (status) {
    // 422 = server đã QUA tầng auth rồi mới chê payload probe ⇒ token hợp lệ VÀ đường
    // gửi thông. Đây là ca XANH mong đợi, không phải lỗi.
    case 422:
      return {
        ...base,
        status: "ok",
        detail:
          "422 — token hợp lệ, đường gửi thông (server nhận request, chỉ chê payload probe cố ý sai).",
      };
    // 429 cũng nằm SAU tầng auth ⇒ vẫn chứng minh token tốt.
    case 429:
      return {
        ...base,
        status: "warn",
        detail: "429 — token hợp lệ, đường gửi thông, nhưng đang bị rate limit. Thử lại sau.",
      };
    case 401:
      return {
        ...base,
        status: "fail",
        detail: "401 — token hỏng/hết hạn. Chạy `avatar login` rồi thử lại.",
      };
    // 200 nghĩa là payload probe LỌT qua validate — schema server đã đổi, probe không
    // còn vô hại nữa. Cảnh báo để người sửa biết phải chỉnh payload.
    case 200:
      return {
        ...base,
        status: "warn",
        detail:
          "200 — server CHẤP NHẬN payload probe (đáng lẽ 422). Schema server có thể đã đổi; báo team telemetry.",
      };
    default:
      return {
        ...base,
        status: "warn",
        detail: `HTTP ${status ?? "?"} — không nằm trong các mã đã biết (401/422/429).`,
      };
  }
}

/** Chạy probe rồi dựng CheckResult. `python3` TRẦN — đúng binary hook telemetry dùng. */
export async function buildTelemetryDeliveryProbeCheck(cwd: string): Promise<CheckResult> {
  const outcome = await runProbe(cwd);
  return { name: CHECK_NAME, ...classifyProbeOutcome(outcome) };
}

function runProbe(cwd: string): Promise<ProbeOutcome> {
  return new Promise((resolve) => {
    const shared = join(cwd, ".claude", "pack", "tools", "telemetry", "hooks", "shared");
    let child: ReturnType<typeof spawn>;
    try {
      child = spawn("python3", ["-c", TELEMETRY_DELIVERY_PROBE_PYTHON_SOURCE], {
        cwd,
        env: { ...process.env, AVATAR_PROBE_PACK_SHARED: shared },
        stdio: ["ignore", "pipe", "pipe"],
      });
    } catch {
      resolve({ kind: "no_python" });
      return;
    }

    let stdout = "";
    let done = false;
    const finish = (o: ProbeOutcome) => {
      if (done) return;
      done = true;
      resolve(o);
    };

    const timer = setTimeout(() => {
      child.kill("SIGKILL");
      finish({ kind: "timeout" });
    }, PROBE_TIMEOUT_MS);

    child.stdout?.on("data", (c: Buffer) => {
      stdout += c.toString();
    });
    child.on("error", () => {
      clearTimeout(timer);
      finish({ kind: "no_python" });
    });
    child.on("close", () => {
      clearTimeout(timer);
      finish(parseProbeStdout(stdout));
    });
  });
}

/** stdout → ProbeOutcome. Script in 1 dòng JSON; rác/rỗng ⇒ `unknown`, không ném. */
export function parseProbeStdout(stdout: string): ProbeOutcome {
  const line = stdout.trim().split("\n").pop()?.trim();
  if (!line) return { kind: "unknown", error_type: "empty_stdout" };
  try {
    const parsed = JSON.parse(line) as ProbeOutcome;
    return typeof parsed?.kind === "string"
      ? parsed
      : { kind: "unknown", error_type: "no_kind_field" };
  } catch {
    return { kind: "unknown", error_type: "unparsable_stdout" };
  }
}

/**
 * ProbeOutcome → PythonCaStoreState. Probe bản cũ không gửi 4 trường này ⇒ để
 * `opensslCafile` null, `planCaStoreRepair` sẽ ra `unknown_state` thay vì bịa ra một
 * đích symlink rồi ghi đè nhầm.
 */
export function readCaStoreState(outcome: ProbeOutcome): PythonCaStoreState {
  return {
    cafile: outcome.ca_cafile ?? null,
    opensslCafile: outcome.ca_openssl_cafile ?? null,
    certifiPath: outcome.certifi_path ?? null,
    certifiExists: outcome.certifi_exists === true,
    opensslCafileExists: outcome.ca_openssl_cafile_exists === true,
    sslCertFileEnv: outcome.ca_ssl_cert_file_env ?? null,
  };
}

/** `doctor --fix` gọi vào đây. Ném khi không vá được để doctor in đỏ, kèm lệnh sudo. */
async function runCaStoreFix(plan: CaStoreRepairPlan): Promise<void> {
  const result = await applyCaStoreRepair(plan);
  if (result.ok) return;
  throw new Error(
    result.sudoCommand ? `${result.message} Chạy tay: ${result.sudoCommand}` : result.message,
  );
}
